import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { db, auth, getCollectionName } from './firebase';
import { collection, onSnapshot, query, addDoc, where, getDocs, doc, updateDoc, deleteDoc, writeBatch } from './firestore-compat';
import { addDays, addWeeks, addMonths, startOfWeek, format, isBefore, isEqual } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import AddDirecaoModal from './AddDirecaoModal';
import EditDirecaoModal from './EditDirecaoModal';
import DeadlineReminder from './components/DeadlineReminder';
import { useDeadlineReminder } from './hooks/useDeadlineReminder';
import { hasPermission } from './utils/roleUtils';
import { notifyParticipantsOfChange } from './utils/changeNotificationUtils';

const RESPONSAVEIS = {
  ANA: 'Ana Paula',
  MARIO: 'Dr. Mário',
  DIRETORIA: 'Diretoria'
};

const formatToYYYYMMDD = (date) => format(date, 'yyyy-MM-dd');

const getWeekDays = (date) => {
  const sunday = startOfWeek(date);
  const weekDays = [];
  for (let i = 1; i <= 5; i++) {
    weekDays.push(addDays(sunday, i));
  }
  return weekDays;
};

// EMERGENCIAL: Usuário solicitou ver dados de PRODUÇÃO no ambiente de TESTE.
// Removendo o uso de getCollectionName para esta collection específica ou forçando o nome de produção.
const COLLECTION_NAME = 'agendaDirecao'; // Nome da coleção de produção

function AgendaDirecao() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [eventos, setEventos] = useState([]);

  // Ref para controle de cliques (debouncing)
  const lastAddRef = React.useRef(0);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [userRole, setUserRole] = useState(null);
  const [userName, setUserName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState({});

  const currentUser = auth.currentUser;

  // Hook para detectar mobile (DEVE estar antes de qualquer return)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const eventsForReminder = useMemo(() => {
    return eventos.map(e => ({
      ...e,
      start: e.data,
      title: e.descricao
    }));
  }, [eventos]);

  const { isTodayOpen, isWeekOpen, todayItems, weekItems, closeTodayPopup, closeWeekPopup, dismissToday, openReminderManually } = useDeadlineReminder(eventsForReminder, 'agenda_direcao', 'event');

  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser && currentUser.email) {
        const usersRef = collection(db, getCollectionName('users'));
        const q = query(usersRef, where('email', '==', currentUser.email));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const userData = querySnapshot.docs[0].data();
          // Normalizar: roles (array) ou role (string legada)
          const role = Array.isArray(userData.roles) && userData.roles.length > 0
            ? userData.roles[0] : (userData.role || 'leitor');
          setUserRole(role);
          setUserName(userData.name);
          setUserPermissions(userData.permissions || {});
        } else {
          setUserRole('leitor');
          setUserPermissions({});
        }
      }
      setIsLoading(false);
    };
    fetchUserData();

    // ATENÇÃO: Lendo diretamente da produção conforme solicitado
    const qEventos = query(collection(db, COLLECTION_NAME));
    const unsubscribe = onSnapshot(qEventos, async (snapshot) => {
      const allDocs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      // ========== DEDUPLICAÇÃO AUTOMÁTICA ==========
      // Gera uma "chave" para cada evento baseada nos campos que definem identidade.
      // Se dois eventos têm a mesma chave, o segundo (e seguintes) são duplicatas.
      const seen = new Map(); // chave -> primeiro evento
      const duplicateIds = [];

      for (const evt of allDocs) {
        const key = [
          evt.data || '',
          evt.horario || '',
          (evt.descricao || '').trim().toLowerCase(),
          (evt.responsaveis || []).slice().sort().join(',')
        ].join('|');

        if (seen.has(key)) {
          // Este é um duplicado – marcar para remoção
          duplicateIds.push(evt.id);
        } else {
          seen.set(key, evt);
        }
      }

      // Excluir duplicatas do Firestore em segundo plano (não bloqueia a UI)
      if (duplicateIds.length > 0) {
        console.warn(`[DEDUP] Encontradas ${duplicateIds.length} duplicata(s). Excluindo:`, duplicateIds);
        for (const dupId of duplicateIds) {
          try {
            await deleteDoc(doc(db, COLLECTION_NAME, dupId));
            console.log(`[DEDUP] Excluído duplicado: ${dupId}`);
          } catch (err) {
            console.error(`[DEDUP] Falha ao excluir duplicado ${dupId}:`, err);
          }
        }
      }

      // Exibir apenas os eventos únicos (sem duplicatas) e com estrutura válida
      const uniqueDocs = allDocs
        .filter(e => !duplicateIds.includes(e.id))
        .map(e => ({
          ...e,
          // Garante que responsaveis SEMPRE é um array
          responsaveis: Array.isArray(e.responsaveis) ? e.responsaveis : [],
          // Garante que horario SEMPRE é string
          horario: e.horario || '',
          // Garante descricao
          descricao: e.descricao || e.titulo || '',
        }));
      setEventos(uniqueDocs);
    }, (error) => {
      console.error("Erro ao buscar eventos da agenda:", error);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // ==========================================
  // INTEGRAÇÃO COM EVENTOS EXTERNOS
  // ==========================================
  const [externalEvents, setExternalEvents] = useState([]);

  useEffect(() => {
    const DIRETORES_NAMES = ['Ana Paula', 'Dr. Mário', 'Dr Mario', 'Ana Paula (Diretora)'];
    // UIDs conhecidos dos diretores (para match quando só o UID é salvo)
    // TODO: idealmente buscar do banco, mas hardcoded resolve o imediato
    const DIRETORES_EMAILS = ['apaulacf2020@gmail.com', 'mario@prontobaby.com.br'];

    // Buscar UIDs dos diretores para cruzar com participantesUids
    const fetchAndListen = async () => {
      // Buscar UIDs dos diretores por email
      const diretoresUids = new Set();
      for (const email of DIRETORES_EMAILS) {
        const snap = await getDocs(query(collection(db, getCollectionName('users')), where('email', '==', email)));
        snap.forEach(d => diretoresUids.add(d.id));
      }
      // Também buscar por nome
      for (const name of DIRETORES_NAMES) {
        const snap = await getDocs(query(collection(db, getCollectionName('users')), where('name', '==', name)));
        snap.forEach(d => diretoresUids.add(d.id));
      }

      const qAuditorias = query(collection(db, 'auditorias'));

      return onSnapshot(qAuditorias, (snap) => {
        const events = [];
        snap.forEach(docSnap => {
          const data = docSnap.data();

          // 1. Check por nome nos campos textuais
          const parts = [
            ...(data.auditores || []),
            ...(Array.isArray(data.participantes) ? data.participantes : []),
            ...(data.participantesNomes || []),
            ...(data.responsaveis || [])
          ];
          // Se participantes é string, verificar diretamente
          const partString = typeof data.participantes === 'string' ? data.participantes : '';

          const hasDirectorByName = parts.some(p => {
            if (typeof p !== 'string') return false;
            return DIRETORES_NAMES.some(dir => p.includes(dir) || dir.includes(p));
          }) || DIRETORES_NAMES.some(dir => partString.includes(dir));

          // 2. Check por UID nos participantesUids
          const uids = data.participantesUids || [];
          const hasDirectorByUid = uids.some(uid => diretoresUids.has(uid));

          if (hasDirectorByName || hasDirectorByUid) {
            events.push({
              id: docSnap.id,
              ...data,
              type: 'external',
              source: 'Qualidade/Outros',
              isExternal: true
            });
          }
        });
        setExternalEvents(events);
      });
    };

    let unsubExt = null;
    fetchAndListen().then(unsub => { unsubExt = unsub; });

    return () => { if (unsubExt) unsubExt(); };
  }, [currentDate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePrevWeek = () => setCurrentDate(prev => addDays(prev, -7));
  const handleNextWeek = () => setCurrentDate(prev => addDays(prev, 7));

  const checkForConflict = async (data, horario, responsaveis, excludeId = null) => {
    const q = query(
      collection(db, COLLECTION_NAME),
      where('data', '==', data),
      where('horario', '==', horario)
    );
    const snap = await getDocs(q);

    for (const docSnap of snap.docs) {
      if (docSnap.id === excludeId) continue;
      const eventData = docSnap.data();

      if (eventData.status === 'Não Realizado' || eventData.status === 'Cancelado') continue;

      const eventResponsaveis = eventData.responsaveis || [];
      const hasConflict = responsaveis.some(r => eventResponsaveis.includes(r));

      if (hasConflict) return true;
    }
    return false;
  };

  const handleAddEvento = async (novoEvento, recorrenciaData) => {
    // Proteção extra contra cliques múltiplos (debounce de 2 segundos)
    const now = Date.now();
    if (now - lastAddRef.current < 2000) {
      console.warn("Clique duplo detectado no handleAddEvento, ignorando.");
      return;
    }
    lastAddRef.current = now;

    if (!recorrenciaData.isRecorrente) {
      const hasConflict = await checkForConflict(novoEvento.data, novoEvento.horario, novoEvento.responsaveis);
      if (hasConflict) {
        throw new Error("CONFLITO_HORARIO");
      }
      await addDoc(collection(db, COLLECTION_NAME), novoEvento);
      return;
    }

    // Lógica de Recorrência (Criação em lote)
    const batch = writeBatch(db);
    let dataAtual = new Date(novoEvento.data + 'T00:00:00');
    const dataFinal = new Date(recorrenciaData.terminaEm + 'T00:00:00');

    let diasComConflito = 0;

    while (isBefore(dataAtual, dataFinal) || isEqual(dataAtual, dataFinal)) {
      // Pula fins de semana (Sábado=6, Domingo=0)
      if (dataAtual.getDay() !== 6 && dataAtual.getDay() !== 0) {
        const dataStr = dataAtual.toISOString().split('T')[0];

        // Verifica conflito antes de adicionar ao batch
        const hasConflict = await checkForConflict(dataStr, novoEvento.horario, novoEvento.responsaveis);

        if (!hasConflict) {
          const docRef = doc(collection(db, COLLECTION_NAME));
          batch.set(docRef, {
            ...novoEvento,
            data: dataStr
          });
        } else {
          diasComConflito++;
        }
      }

      // Incremento baseado na frequência
      switch (recorrenciaData.frequencia) {
        case 'diaria': dataAtual = addDays(dataAtual, 1); break;
        case 'semanal': dataAtual = addWeeks(dataAtual, 1); break;
        case 'quinzenal': dataAtual = addWeeks(dataAtual, 2); break;
        case 'mensal': dataAtual = addMonths(dataAtual, 1); break;
        case 'anual': dataAtual = addDays(dataAtual, 365); break; // Aproximação ou addYears
        default: dataAtual = addDays(dataFinal, 1);
      }
    }
    await batch.commit();

    if (diasComConflito > 0) {
      alert(`O agendamento recorrente foi criado, mas ${diasComConflito} dia(s) foram pulados porque já havia outro compromisso no mesmo horário para os mesmos responsáveis.`);
    }
  };

  const handleOpenEditModal = (evento) => {
    setSelectedEvent(evento);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedEvent(null);
  };

  const handleUpdateEvento = async (eventoId, eventoAtualizado) => {
    try {
      const hasConflict = await checkForConflict(eventoAtualizado.data, eventoAtualizado.horario, eventoAtualizado.responsaveis, eventoId);
      if (hasConflict) {
        throw new Error("CONFLITO_HORARIO");
      }

      const eventRef = doc(db, COLLECTION_NAME, eventoId);
      await updateDoc(eventRef, eventoAtualizado);

      // --- NOVO: Notificar participantes se data ou hora mudou ---
      if (selectedEvent) {
        await notifyParticipantsOfChange(
          { ...selectedEvent, id: eventoId }, // Garantir que ID está presente
          eventoAtualizado,
          userName || currentUser.displayName || 'Usuário',
          COLLECTION_NAME
        );

        // Sincronizar mudança de data/hora nas cópias da agenda_pessoal
        // Busca por AMBOS os campos: 'sourceRef' (cópias de aceite) e 'originalEventId' (legado)
        const dateChanged = eventoAtualizado.data && eventoAtualizado.data !== selectedEvent.data;
        const timeChanged = eventoAtualizado.horario && eventoAtualizado.horario !== selectedEvent.horario;
        if (dateChanged || timeChanged) {
          try {
            const updates = {};
            if (dateChanged) updates.data = eventoAtualizado.data;
            if (timeChanged) updates.horario = eventoAtualizado.horario;
            if (eventoAtualizado.descricao) updates.descricao = eventoAtualizado.descricao;

            // Buscar cópias pelo campo 'sourceRef' (cópias criadas por aceite de convite)
            const qBySourceRef = query(collection(db, 'agenda_pessoal'), where('sourceRef', '==', eventoId));
            const snapBySourceRef = await getDocs(qBySourceRef);

            // Buscar cópias pelo campo 'originalEventId' (cópias legadas)
            const qByOriginalId = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', eventoId));
            const snapByOriginalId = await getDocs(qByOriginalId);

            // Unir todas as cópias encontradas (evitar duplicatas por ID)
            const copiasPorId = new Map();
            snapBySourceRef.forEach(d => copiasPorId.set(d.id, d));
            snapByOriginalId.forEach(d => copiasPorId.set(d.id, d));

            const updatePromises = [];
            copiasPorId.forEach((d) => {
              updatePromises.push(updateDoc(doc(db, 'agenda_pessoal', d.id), updates));
            });

            if (updatePromises.length > 0) {
              await Promise.all(updatePromises);
              console.log(`[SYNC] ${updatePromises.length} cópia(s) na agenda_pessoal atualizadas com a nova data/hora.`);
            } else {
              console.log('[SYNC] Nenhuma cópia encontrada na agenda_pessoal para este evento.');
            }
          } catch (pesErr) {
            console.error('[SYNC ERROR] Erro ao sincronizar data/hora para agenda_pessoal:', pesErr);
          }
        }
      }
      // --------------------------------------------------------
    } catch (error) {
      // Se o documento não existe (ex: foi excluído pela deduplicação),
      // usar os dados do modal aberto para encontrar o evento que sobreviveu.
      if (error.message && error.message.includes('No document to update')) {
        console.warn(`[UPDATE] Doc ${eventoId} não encontrado. Buscando equivalente no Firestore...`);

        // Usar os dados do selectedEvent (o que estava aberto no modal)
        const evtAberto = selectedEvent;
        if (evtAberto && evtAberto.data && evtAberto.descricao) {
          try {
            // Buscar no Firestore por eventos com mesma data e descrição
            const qBusca = query(
              collection(db, COLLECTION_NAME),
              where('data', '==', evtAberto.data),
              where('descricao', '==', evtAberto.descricao)
            );
            const snapBusca = await getDocs(qBusca);

            if (!snapBusca.empty) {
              // Usar o primeiro resultado encontrado
              const docSobrevivente = snapBusca.docs[0];
              console.log(`[UPDATE] Encontrado evento equivalente: ${docSobrevivente.id}. Atualizando...`);
              await updateDoc(doc(db, COLLECTION_NAME, docSobrevivente.id), eventoAtualizado);
              return; // Sucesso!
            }
          } catch (buscaErr) {
            console.error('[UPDATE] Erro na busca por equivalente:', buscaErr);
          }
        }

        // Se nada funcionou, avisar
        alert('Este agendamento não foi encontrado. Feche o modal e tente novamente.');
        return;
      }
      throw error;
    }
  };

  const handleDeleteEvento = async (eventoId) => {
    const eventRef = doc(db, COLLECTION_NAME, eventoId);

    // Identificar a origem para limpeza (se for cópia)
    const evento = eventos.find(e => e.id === eventoId);
    const originalId = evento?.sourceRef || evento?.originalEventId || eventoId;

    await deleteDoc(eventRef);

    // Limpeza profunda de clones
    try {
      await deleteDoc(doc(db, 'agenda_pessoal', 'ext-' + originalId));
    } catch (e) { }

    try {
      const qPes = query(collection(db, 'agenda_pessoal'), where('originalEventId', '==', originalId));
      const snapPes = await getDocs(qPes);
      snapPes.forEach(async (d) => await deleteDoc(doc(db, 'agenda_pessoal', d.id)));
    } catch (e) { }
  };

  // Função para reagendar evento recusado
  const handleReagendar = (eventoId, eventoCollection) => {
    // Buscar o evento para editar
    const evento = eventos.find(e => e.id === eventoId);
    if (evento) {
      handleOpenEditModal(evento);
    } else {
      alert('Evento não encontrado. Pode ter sido excluído.');
    }
  };

  // handleDuplicateEvento removido em favor da recorrência no modal


  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  if (isLoading) {
    return <div>A carregar permissões...</div>;
  }

  // Função auxiliar para renderizar os cartões de evento
  const renderEventos = (eventos, colunaOwner) => {
    return eventos
      .filter(e => e && e.responsaveis && Array.isArray(e.responsaveis))
      .sort((a, b) => (a.horario || '').localeCompare(b.horario || ''))
      .map(evento => {

        const isAna = evento.responsaveis.includes(RESPONSAVEIS.ANA);
        const isMario = evento.responsaveis.includes(RESPONSAVEIS.MARIO);
        const isDiretoria = evento.responsaveis.includes(RESPONSAVEIS.DIRETORIA);

        // Um evento é "de grupo" se for da Diretoria ou se tiver Ana E Mário
        const isGroupEvent = isDiretoria || (isAna && isMario);

        let cardClass = 'evento-card-direcao';

        // Cor base por responsável
        if (isAna && isMario) {
          cardClass += ' resp-ambos';       // Preto: ambos
        } else if (isDiretoria && !isAna && !isMario) {
          cardClass += ' resp-diretoria';   // Amarelo: só Diretoria
        } else if (isDiretoria || (isAna && isMario)) {
          cardClass += ' resp-ambos';       // Preto: grupo
        } else if (isAna) {
          cardClass += ' resp-ana';          // Lilás: só Ana
        } else if (isMario) {
          cardClass += ' resp-mario';        // Azul: só Mário
        }

        if (isGroupEvent) {
          cardClass += ' group-event';
          if (colunaOwner === RESPONSAVEIS.ANA) {
            cardClass += ' group-event-left';
          } else if (colunaOwner === RESPONSAVEIS.MARIO) {
            cardClass += ' group-event-right';
          }
        }

        // Status visual: verde se realizado, vermelho se passada e não realizado
        if (evento.status === 'Feito') {
          cardClass += ' status-feito';
        } else if (evento.status === 'Não Realizado' || evento.justificativaNaoRealizado) {
          cardClass += ' status-nao-realizado';
        } else {
          const hoje = new Date();
          hoje.setHours(0, 0, 0, 0);
          const dataEvento = new Date(evento.data + 'T00:00:00');
          if (dataEvento < hoje && (!evento.status || evento.status === 'Agendado')) {
            cardClass += ' status-nao-realizado';
          }
        }

        return (
          <div
            key={evento.id}
            className={cardClass}
            onClick={() => handleOpenEditModal(evento)}
          >
            <span className="event-time">{evento.horario || '—'}</span>
            <span className="event-description">{evento.descricao || 'Sem título'}</span>
            {evento.setor && <span className="event-detail">Setor: {evento.setor}</span>}
            {evento.participantes && <span className="event-detail">Com: {evento.participantes}</span>}
            <span className="event-detail responsaveis">
              Para: {(evento.responsaveis || []).join(', ')}
            </span>
          </div>
        );
      });
  };

  // Layout 1: Visão da Secretária (grade 10 colunas × N linhas de horário)
  const renderSecretariaLayout = () => {
    // Coletar TODOS os horários únicos da semana inteira
    // Filtra eventos sem horario definido para evitar crash no sort
    const allWeekTimes = [...new Set(
      eventos
        .filter(e => weekDays.some(d => formatToYYYYMMDD(d) === e.data) && e.horario)
        .map(e => e.horario)
    )].sort();

    return (
      <div className="agenda-direcao-container">
        {/* Cabeçalho: 10 colunas (2 por dia) */}
        <div className="agenda-direcao-header-10col">
          {weekDays.map(day => (
            <React.Fragment key={day.toISOString()}>
              <div className="day-header-direcao">
                <span className="day-name">{format(day, 'EEEE', { locale: ptBR })}</span>
                <span className="day-date">{format(day, 'dd/MM')}</span>
                <span className="day-owner">Ana Paula</span>
              </div>
              <div className="day-header-direcao">
                <span className="day-name">{format(day, 'EEEE', { locale: ptBR })}</span>
                <span className="day-date">{format(day, 'dd/MM')}</span>
                <span className="day-owner">Dr. Mário</span>
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Grade: 10 colunas, 1 linha por faixa horária global */}
        <div className="agenda-direcao-grid-10col">
          {allWeekTimes.length === 0 ? (
            weekDays.map(day => (
              <React.Fragment key={formatToYYYYMMDD(day)}>
                <div className="time-slot-cell"><div className="time-slot-empty">—</div></div>
                <div className="time-slot-cell"><div className="time-slot-empty">—</div></div>
              </React.Fragment>
            ))
          ) : (
            allWeekTimes.map(time => (
              <React.Fragment key={time}>
                {weekDays.map(day => {
                  const dayKey = formatToYYYYMMDD(day);
                  const eventosDoDia = eventos.filter(e => e.data === dayKey);

                  const evtsAna = eventosDoDia.filter(e =>
                    e.horario === time &&
                    Array.isArray(e.responsaveis) &&
                    (e.responsaveis.includes(RESPONSAVEIS.ANA) || e.responsaveis.includes(RESPONSAVEIS.DIRETORIA))
                  );
                  const evtsMario = eventosDoDia.filter(e =>
                    e.horario === time &&
                    Array.isArray(e.responsaveis) &&
                    (e.responsaveis.includes(RESPONSAVEIS.MARIO) || e.responsaveis.includes(RESPONSAVEIS.DIRETORIA))
                  );

                  return (
                    <React.Fragment key={dayKey}>
                      <div className="time-slot-cell">
                        {evtsAna.length > 0 ? (
                          renderEventos(evtsAna, RESPONSAVEIS.ANA)
                        ) : (
                          <div className="time-slot-empty">
                            <span className="time-label-ghost">{time}</span>
                          </div>
                        )}
                      </div>
                      <div className="time-slot-cell">
                        {evtsMario.length > 0 ? (
                          renderEventos(evtsMario, RESPONSAVEIS.MARIO)
                        ) : (
                          <div className="time-slot-empty">
                            <span className="time-label-ghost">{time}</span>
                          </div>
                        )}
                      </div>
                    </React.Fragment>
                  );
                })}
              </React.Fragment>
            ))
          )}
        </div>
      </div>
    );
  };

  // Layout 2: Visão do Diretor (5 colunas)
  const renderDiretorLayout = () => (
    <div className="agenda-direcao-container">
      <div className="agenda-direcao-header-5col">
        {weekDays.map(day => (
          <div className="day-header-direcao" key={day.toISOString()}>
            <span className="day-name">{format(day, 'EEEE', { locale: ptBR })}</span>
            <span className="day-date">{format(day, 'dd/MM')}</span>
          </div>
        ))}
      </div>
      <div className="agenda-direcao-grid-5col">
        {weekDays.map(day => {
          const dayKey = formatToYYYYMMDD(day);
          const eventosDoDia = eventos.filter(e =>
            e.data === dayKey &&
            Array.isArray(e.responsaveis) &&
            (e.responsaveis.includes(agendaUserName) || e.responsaveis.includes(RESPONSAVEIS.DIRETORIA))
          );
          return (
            <div key={dayKey} className="day-cell-direcao">{renderEventos(eventosDoDia, agendaUserName)}</div>
          );
        })}
      </div>
    </div>
  );

  // Layout Mobile: Lista Simples (Dia -> Eventos)
  const renderMobileLayout = () => (
    <div className="agenda-direcao-mobile-list">
      {weekDays.map(day => {
        const dayKey = formatToYYYYMMDD(day);
        const eventosDoDia = eventos.filter(e => e.data === dayKey);
        // Mostra tudo se for admin/secretária, ou filtra se for diretor
        const filteredEvents = isSecretariaView
          ? eventosDoDia
          : eventosDoDia.filter(e => e.responsaveis.includes(userName) || e.responsaveis.includes(RESPONSAVEIS.DIRETORIA));

        if (filteredEvents.length === 0) return null;

        return (
          <div key={dayKey} className="mobile-day-group" style={{ marginBottom: '20px', backgroundColor: '#fff', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px', color: '#00897b' }}>
              {format(day, 'EEEE, dd/MM', { locale: ptBR })}
            </h4>
            <div className="mobile-events-list">
              {renderEventos(filteredEvents, 'mobile')}
            </div>
          </div>
        );
      })}
      {weekDays.every(day => {
        const dayKey = formatToYYYYMMDD(day);
        const eventosDoDia = eventos.filter(e => e.data === dayKey);
        const filtered = isSecretariaView ? eventosDoDia : eventosDoDia.filter(e => e.responsaveis.includes(userName) || e.responsaveis.includes(RESPONSAVEIS.DIRETORIA));
        return filtered.length === 0;
      }) && (
          <div className="text-center p-4 text-muted">Nenhum compromisso nesta semana.</div>
        )}
    </div>
  );

  // Permissões
  const hasViewPermission = userPermissions?.agenda_direcao?.view;
  const hasEditPermission = userPermissions?.agenda_direcao?.edit;

  // Mapeamento de Emails/Nomes para os Nomes da Agenda
  const AGENDA_NAMES_MAP = {
    'marioviana@terra.com.br': RESPONSAVEIS.MARIO,
    'anapaula@iwgroup.com.br': RESPONSAVEIS.ANA, // Exemplo, ajustar se necessário
    'Ana Paula': RESPONSAVEIS.ANA,
    'Dr. Mário': RESPONSAVEIS.MARIO,
    'Dr. Mário Eduardo': RESPONSAVEIS.MARIO
  };

  const getAgendaName = (user) => {
    if (!user) return '';
    if (AGENDA_NAMES_MAP[user.email]) return AGENDA_NAMES_MAP[user.email];
    if (AGENDA_NAMES_MAP[user.name]) return AGENDA_NAMES_MAP[user.name];
    if (user.name === RESPONSAVEIS.ANA || user.name === RESPONSAVEIS.MARIO) return user.name;
    return user.name;
  };

  const agendaUserName = getAgendaName({ email: currentUser?.email, name: userName });

  // Em localhost, forçamos a permissão para facilitar o desenvolvimento/correção visual
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

  // Verifica permissões básicas via RBAC
  const canViewAgenda = hasPermission(userRole, 'view_agenda_direcao', userPermissions) || isLocalhost;

  // MUDANÇA: Diretoria AGORA PODE EDITAR TUDO (pedido do usuário)
  // Antes: Só Admin/Secretária e "edit_agenda_direcao_all"
  // Agora: Diretoria também entra aqui.
  const isDiretoriaRole = hasPermission(userRole, 'diretoria'); // Check básico de role

  const isSuper = currentUser && currentUser.email === 'alexandrebareaoliveira@gmail.com';

  const canEditAll = isSuper || hasPermission(userRole, 'edit_agenda_direcao_all', userPermissions) ||
    userPermissions?.agenda_direcao?.edit === true ||
    userRole === 'diretoria' || // Diretoria agora tem poder de edição total
    isLocalhost;

  // Permissão para editar APENAS sua própria coluna (Mantido para compatibilidade, mas redundante se Diretoria edita tudo)
  const canEditOwn = hasPermission(userRole, 'edit_agenda_direcao_own', userPermissions);

  // Lógica de Visualização
  // Se pode editar tudo (Secretária/Admin), vê o layout completo (10 colunas)
  const isSecretariaView = canEditAll;

  // Se não é secretária/admin, mas tem permissão de view, assume visão de diretor (5 colunas)
  const isDiretorView = !isSecretariaView && canViewAgenda;

  return (
    <div className="agenda-container">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ color: '#555', margin: 0 }}>Agenda da Direção</h2>
        <div className="header-actions">
          <button className="reminder-btn" style={{ backgroundColor: '#e67e22', color: 'white', marginRight: '10px' }} onClick={openReminderManually}>
            🔔 Próximos Vencimentos
          </button>
          {(canEditAll || canEditOwn) && (
            <button className="novo-agendamento-btn" onClick={() => setIsAddModalOpen(true)}>
              + Agendar
            </button>
          )}
        </div>
      </div>

      <main className="agenda-main">
        <div className="week-navigation" style={{ justifyContent: 'center', marginBottom: '20px' }}>
          <button onClick={handlePrevWeek}>&lt; Semana Anterior</button>
          <span style={{ minWidth: '250px' }}>
            Semana de {format(weekDays[0], 'dd/MM')} a {format(weekDays[4], 'dd/MM/yyyy')}
          </span>
          <button onClick={handleNextWeek}>Próxima Semana &gt;</button>
        </div>

        {isMobile ? (
          renderMobileLayout()
        ) : (
          <>
            {isSecretariaView && renderSecretariaLayout()}
            {isDiretorView && renderDiretorLayout()}
          </>
        )}

        {!isSecretariaView && !isDiretorView && !isMobile && (
          <p style={{ textAlign: 'center', padding: '50px' }}>Você não tem permissão para ver esta agenda.</p>
        )}
      </main>

      <AddDirecaoModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAddEvento={handleAddEvento}
        userRole={userRole}
        selectedDate={currentDate}
        canEditAll={canEditAll}
        canEditOwn={canEditOwn}
        userPermissions={userPermissions}
      />

      <EditDirecaoModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onUpdateEvento={handleUpdateEvento}
        onDeleteEvento={handleDeleteEvento}
        evento={selectedEvent}
        userRole={userRole}
        canEditAll={canEditAll}
        canEditOwn={canEditOwn}
        userPermissions={userPermissions}
      />
      <DeadlineReminder
        isTodayOpen={isTodayOpen}
        isWeekOpen={isWeekOpen}
        todayItems={todayItems}
        weekItems={weekItems}
        onCloseTodayPopup={closeTodayPopup}
        onCloseWeekPopup={closeWeekPopup}
        onDismissToday={dismissToday}
        type="event"
      />
    </div>
  );
}

export default AgendaDirecao;